import React from 'react';
import {
  Platform,
  TouchableOpacity,
  View,
  Text,
  ActivityIndicator,
  StyleSheet,
} from 'react-native';

import Icon from 'shared/modules/common/components/atoms/Icon';
import colors from 'shared/modules/common/constants/colors';
import typography from 'shared/modules/common/constants/typography';
import generateID from 'shared/modules/common/utils/generate-id';
import Card from 'shared/modules/usage/components/molecules/Card';

type ListSectionProps = {
  title: string;
  loading?: boolean;
  children: JSX.Element | Array<JSX.Element | null> | null;
  emptyMessage: string;
  errorMessage: string;
  bottomLinkText?: string;
  onBottomLinkPress?: () => void;
  style?: ViewStyleProp;
};

export default function ListSection({
  title,
  loading,
  emptyMessage,
  errorMessage,
  children,
  onBottomLinkPress,
  bottomLinkText,
  style,
}: ListSectionProps) {
  const renderContent = () => {
    if (loading) {
      return (
        <ActivityIndicator
          color={colors.greySpinner}
          style={styles.alignCenter}
        />
      );
    }

    if (emptyMessage || errorMessage) {
      return (
        <Text style={[styles.message, styles.alignCenter]}>
          {emptyMessage ? emptyMessage : errorMessage}
        </Text>
      );
    }

    return children;
  };

  return (
    <View style={[styles.container, style]}>
      <View style={styles.headerContainer}>
        <Text style={styles.title}>{title}</Text>
      </View>

      <Card>{renderContent()}</Card>

      {!!bottomLinkText && (
        <View style={styles.bottomLinkContainer}>
          <TouchableOpacity
            onPress={onBottomLinkPress}
            /** Generate HTML and Test IDs  */
            {...generateID('list-section-bottom-link-button')} // Name too generic and can cause ID clashes, this must be customizable
            /** Platform-specific props */
            {...Platform.select({
              web: {
                accessibilityRole: 'button',
              },
            })}
          >
            <Text style={styles.bottomLinkText}>{bottomLinkText}</Text>
          </TouchableOpacity>
          <Icon size="16" name="caret-right" />
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginTop: 24,
  },
  headerContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  title: {
    ...typography.bodyBold,
    color: colors.teal,
  },
  message: {
    ...typography.caption02,
    color: colors.greyMedium,
  },
  bottomLinkContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginTop: 16,
  },
  bottomLinkText: {
    ...typography.caption01,
    color: colors.greyDark,
  },
  alignCenter: {
    alignSelf: 'center',
  },
});
